import type { CSSProperties } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  FileCheck,
  GitCommit,
  Image,
  MessageSquare,
  Package,
  Radio,
  Rocket,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { AgentAction } from './types';

export interface ActionStyle {
  Icon: LucideIcon;
  bg: string;
  text: string;
  border: string;
}

export interface ActionVisual {
  Icon: LucideIcon;
  circleClass: string;
  circleStyle?: CSSProperties;
  iconClass: string;
  iconStyle?: CSSProperties;
  errorBorder: boolean;
  rowTintClass: string;
  rowBorderClass: string;
}

function resolveToolName(action: Pick<AgentAction, 'tool_name' | 'subtitle' | 'title'>): string {
  if (action.tool_name) return action.tool_name;
  const via = action.subtitle?.match(/via (dc__[a-z0-9_]+)/i);
  if (via?.[1]) return via[1];
  const called = action.title?.match(/called (dc__[a-z0-9_]+)/i);
  return called?.[1] || '';
}

function isCallerErrorRow(action: Pick<AgentAction, 'status' | 'subtitle'>): boolean {
  return action.status === 'caller_error' || /Caller error:/i.test(action.subtitle || '');
}

function isStaleResolvedRow(action: Pick<AgentAction, 'status' | 'subtitle'>): boolean {
  const status = String(action.status || '');
  if (status === 'resolved') return true;
  return /(?:^|·\s*)Stale:/i.test(action.subtitle || '');
}

function isFailedCall(action: Pick<AgentAction, 'type' | 'status' | 'success' | 'subtitle'>): boolean {
  if (isCallerErrorRow(action)) return true;
  if (isStaleResolvedRow(action)) return false;
  if (action.success === false) return true;
  const status = String(action.status || '');
  if (status === 'partial_failure') return false;
  if (status === 'failed' || status === 'error' || status === 'fix_failed') return true;
  return false;
}

/** Split `border-*` / `dark:border-*` into row border + matching ring utilities. */
function splitBorderClasses(border: string): { row: string; ring: string } {
  const rowParts: string[] = [];
  const ringParts: string[] = [];
  for (const part of border.split(/\s+/).filter(Boolean)) {
    if (part.startsWith('dark:border-')) {
      rowParts.push(part);
      ringParts.push(part.replace(/^dark:border-/, 'dark:ring-'));
    } else if (part.startsWith('border-')) {
      rowParts.push(part);
      ringParts.push(part.replace(/^border-/, 'ring-'));
    }
  }
  return { row: rowParts.join(' '), ring: ringParts.join(' ') };
}

/** Apply /90 opacity per utility (supports dark: prefixes). */
function tintBackground(bg: string): string {
  return bg
    .split(/\s+/)
    .filter(Boolean)
    .map((cls) => (cls.includes('/') ? cls : `${cls}/90`))
    .join(' ');
}

/** Feed row icon/color — driven by tool_name, type, status, success (not backend color). */
export function getActionStyle(action: AgentAction): ActionStyle {
  const tool = resolveToolName(action).toLowerCase();
  const status = action.status || '';

  // Caller-parameter errors are still errors — do not paint them as success.
  if (isCallerErrorRow(action)) {
    return {
      Icon: AlertTriangle,
      bg: 'bg-amber-50 dark:bg-amber-950/40',
      text: 'text-amber-700 dark:text-amber-300',
      border: 'border-amber-300 dark:border-amber-700',
    };
  }

  // Resolved historical failures (superseded success or stale window)
  if (isStaleResolvedRow(action)) {
    return {
      Icon: CheckCircle2,
      bg: 'bg-green-50 dark:bg-green-950/40',
      text: 'text-green-700 dark:text-green-300',
      border: 'border-green-300 dark:border-green-700',
    };
  }

  // Pending human attention (promotions, template proposals)
  if (status === 'pending_confirmation' || status === 'pending') {
    return {
      Icon: status === 'pending_confirmation' ? Rocket : FileCheck,
      bg: 'bg-amber-50 dark:bg-amber-950/40',
      text: 'text-amber-700 dark:text-amber-300',
      border: 'border-amber-300 dark:border-amber-700',
    };
  }

  // Open failures win over tool-category color so Overview and Agent Actions
  // paint the same tool signature the same way (fix_failed / failed = red).
  if (isFailedCall(action)) {
    return {
      Icon: AlertTriangle,
      bg: 'bg-red-50 dark:bg-red-950/40',
      text: 'text-red-600 dark:text-red-300',
      border: 'border-red-300 dark:border-red-700',
    };
  }

  // 1. Fix loop results
  if (action.type === 'fix' && status === 'fix_pushed') {
    return { Icon: Wrench, bg: 'bg-green-50 dark:bg-green-950/40', text: 'text-green-600 dark:text-green-300', border: 'border-green-300 dark:border-green-700' };
  }
  if (action.type === 'fix' && status === 'error') {
    return { Icon: AlertTriangle, bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-600 dark:text-red-300', border: 'border-red-300 dark:border-red-700' };
  }
  if (action.type === 'fix' && status === 'no_fix') {
    return { Icon: Wrench, bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
  }
  if (action.type === 'fix') {
    return { Icon: Wrench, bg: 'bg-green-50 dark:bg-green-950/40', text: 'text-green-600 dark:text-green-300', border: 'border-green-300 dark:border-green-700' };
  }

  // 2. Tool category styling (wins over MCP success:false for routine calls)
  if (tool.includes('handoff')) {
    return { Icon: MessageSquare, bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
  }
  if (tool.includes('pim') || tool.includes('entity')) {
    return { Icon: Package, bg: 'bg-purple-50 dark:bg-purple-950/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' };
  }
  if (tool.includes('repo')) {
    return { Icon: GitCommit, bg: 'bg-teal-50 dark:bg-teal-950/40', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-700' };
  }
  if (tool.includes('deploy') || tool.includes('promote')) {
    return { Icon: Rocket, bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-600 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-700' };
  }
  if (tool.includes('create_agent') || tool.includes('execute_agent')) {
    return { Icon: Bot, bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-600 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-700' };
  }
  if (tool.includes('template') || tool.includes('binding')) {
    return { Icon: FileCheck, bg: 'bg-teal-50 dark:bg-teal-950/40', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-700' };
  }
  if (tool.includes('iot') || tool.includes('device')) {
    return { Icon: Radio, bg: 'bg-teal-50 dark:bg-teal-950/40', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-700' };
  }
  if (tool.includes('dam') || tool.includes('asset')) {
    return { Icon: Image, bg: 'bg-orange-50 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-700' };
  }

  // 3. Uncategorized failures only (fix errors handled above)
  if (isFailedCall(action)) {
    return { Icon: AlertTriangle, bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-600 dark:text-red-300', border: 'border-red-300 dark:border-red-700' };
  }

  // Deploy/promote audit events without tool_name
  if (action.type === 'deploy' || action.type === 'promote') {
    return { Icon: Rocket, bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-600 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-700' };
  }

  // Handoff audit rows (cursor_handoff) without tool_name in payload
  if (action.type === 'sync' && /handoff note/i.test(action.title || '')) {
    return { Icon: MessageSquare, bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
  }

  // 4. Default — successful but uncategorized
  return { Icon: Zap, bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
}

export function getActionVisual(action: AgentAction): ActionVisual {
  const style = getActionStyle(action);
  const border = splitBorderClasses(style.border);
  return {
    Icon: style.Icon,
    circleClass: `${style.bg} ring-2 ring-inset ${border.ring}`,
    iconClass: style.text,
    errorBorder: style.text.includes('red'),
    rowTintClass: tintBackground(style.bg),
    rowBorderClass: border.row,
  };
}

export function formatRelativeTime(value: string) {
  const diffMs = Date.now() - new Date(value).getTime();
  if (diffMs <= 0) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function formatTimelineClock(value: string) {
  const d = new Date(value);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

export function formatDurationMs(ms: number | null | undefined) {
  if (ms == null) return null;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.round(sec / 60)} min`;
}

export type ViewMode = 'feed' | 'detail' | 'map';
